import uniq from 'lodash/uniq';
import { isBaseHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import type { IssueParent } from '../../../../../../../model/issue-parent/types.tsx';
import type { Card, Project } from '../../../../../../../model/types.tsx';

const EMPTY_ARRAY: Project[] = [];

/**
 * Returns all projects in which the selected cards live
 */
export const getProjectsOfSelection = (selectedCards: Card[], allProjects: Project[]) => {
	const projectIdsOfSelection = uniq(selectedCards.map((card) => card.projectId));
	return allProjects.filter((project) => projectIdsOfSelection.includes(project.id));
};

/**
 * Returns an array of project IDs from which parents
 * can be assigned to the selected cards:
 * - TMP: Only the project in which the selected cards were created
 * - CMP: All CMP projects
 *
 * Assumes that the selected cards have already been validated, i.e:
 * - No cross-project-type selections
 * - TMP selections all belong to the same project
 */
export const getAllPossibleProjectsForSelection = (
	selectedCards: Card[],
	allProjects: Project[],
) => {
	if (!(selectedCards.length && allProjects.length)) {
		return EMPTY_ARRAY;
	}

	const projectsOfSelection = getProjectsOfSelection(selectedCards, allProjects);
	if (!projectsOfSelection.length) {
		return EMPTY_ARRAY;
	}

	const isTmp = projectsOfSelection[0].isSimplified;
	if (isTmp) {
		return [projectsOfSelection[0]];
	}

	return allProjects.filter((project) => !project.isSimplified);
};

/**
 * Filters issue parents by matching issue keys against project keys
 */
export const filterIssueParentsByProjects = (issueParents: IssueParent[], projects: Project[]) => {
	const projectKeys = new Set(projects.map((project) => project.key));
	return issueParents.filter((parent) => {
		const [projectKey] = parent.key.split('-');
		return projectKeys.has(projectKey);
	});
};

/**
 * Returns true if the selected cards are valid for a bulk parent update.
 *
 * Selected cards are valid if:
 * - They do not include any epics or subtasks
 * - They belong to a single project type; either CMP or TMP
 * - If they are TMP cards, they belong to the same TMP project.
 *   Only CMP allows cards to be parented cross-project.
 *
 */
export const validateSelectedCards = (selectedCards: Card[], allProjects: Project[]) => {
	const projectsOfSelection = getProjectsOfSelection(selectedCards, allProjects);
	if (!projectsOfSelection.length) return false;

	const subtasksOrEpicsAreSelected = selectedCards.some(
		({ typeHierarchyLevel }) => !isBaseHierarchyLevel(typeHierarchyLevel),
	);

	const [firstProject, ...remainingProjects] = projectsOfSelection;

	const projectsAreOfDifferentType = remainingProjects.length
		? remainingProjects.some((project) => project.isSimplified !== firstProject.isSimplified)
		: false;

	const multipleTmpProjectsHaveSelections =
		!projectsAreOfDifferentType && firstProject.isSimplified && remainingProjects.length > 0;

	return !(
		subtasksOrEpicsAreSelected ||
		projectsAreOfDifferentType ||
		multipleTmpProjectsHaveSelections
	);
};
